/**
 * CreateBookingWorkshopPicker — the "Choose a workshop" step of the CSR "New
 * booking" modal (BON-142, design 02).
 *
 * The DATA half of the picker: it owns the region + tree filters, the
 * month-scoped fetch and the view mode, and hands the shared
 * {@link WorkshopPicker} a ready list plus a composed filter row. The picker
 * shell owns the dialog, the selection and the day drawer, and is shared
 * verbatim with rebooking (BON-146).
 */
import { useState } from 'react';

import { useViewport } from '../../../hooks/useViewport';

import type { PickedWorkshop } from './CreateBookingModalHelpers';
import { PickerFilters } from './PickerFilters';
import type { PickerViewMode } from './pickerViewMode';
import { useCreateBookingWorkshops } from './useCreateBookingWorkshops';
import { usePickerMonth } from './usePickerMonth';
import { WorkshopPicker } from './WorkshopPicker';
import { regionScopeLabel, toPickedWorkshop } from './WorkshopPickerHelpers';

export interface CreateBookingWorkshopPickerProps {
  /** Return to the New Booking form without picking (Back / footer Cancel). */
  onBack: () => void;
  /** Close the ENTIRE modal (header ×). */
  onClose: () => void;
  onPick: (workshop: PickedWorkshop) => void;
}

export function CreateBookingWorkshopPicker({
  onBack,
  onClose,
  onPick,
}: CreateBookingWorkshopPickerProps) {
  const { isDesktop } = useViewport();
  // Month is owned above the fetch — the fetch is scoped to whichever month is
  // shown, so the month must be resolved first.
  const { month, setMonth } = usePickerMonth();
  const s = useCreateBookingWorkshops(month);
  const [viewMode, setViewMode] = useState<PickerViewMode>(() => (isDesktop ? 'calendar' : 'list'));

  return (
    <WorkshopPicker
      workshops={s.workshops}
      loading={s.loading}
      error={s.error}
      viewMode={viewMode}
      month={month}
      onMonthChange={setMonth}
      headerSubtitle={
        s.confined
          ? 'Your regions, by month — pick the workshop to book this customer into.'
          : 'Every region & month — pick the workshop to book this customer into.'
      }
      filters={
        <PickerFilters
          regions={s.regions}
          regionId={s.regionId}
          onRegionChange={s.setRegionId}
          confined={s.confined}
          treeOptions={s.treeOptions}
          treeSpeciesId={s.treeSpeciesId}
          onTreeChange={s.setTreeSpeciesId}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          month={month}
          onMonthChange={setMonth}
        />
      }
      dayScopeLabel={regionScopeLabel(s.regionId, s.regions)}
      emptyMessage="No upcoming workshops match these filters."
      errorMessage="Could not load workshops."
      onBack={onBack}
      onClose={onClose}
      onPick={(w) => onPick(toPickedWorkshop(w))}
    />
  );
}
